
import React, { useState, useEffect } from "react";
import axios from "axios";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import Loader from "../../Global/Loader";
import PasswordInput from "../../Global/PasswordInput";

function ChangePassword() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });


  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const errorSpan = field.querySelector("span"); // Using <span> instead of <p>
      const cancelButton = field.querySelector(".cancel-button");

      if (input) {
        // Focus animation
        input.addEventListener("focus", () => {
          gsap.to(label, {
            top: -10,
            fontSize: "0.75rem",
            color: "#22c55e",
            duration: 0.25,
          }); // Updated transition time
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          if (errorSpan) {
            errorSpan.classList.remove("text-red-500");
            errorSpan.classList.add("text-gray-500");
          }
        });

        // Blur animation
        input.addEventListener("blur", () => {
          if (!input.value.trim()) {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#4b5563",
              duration: 0.25,
            }); // Updated transition time
          }
          gsap.to(input, { borderColor: "#d1d5db", duration: 0.25 });
        });
      }
    });
  }, []);

  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Trigger validation
    validateForm(name, value);
  };

  const validateForm = (name, value) => {
    let validationErrors = { ...errors };

    // Validate the current password
    if (name === "currentPassword" && value.trim() === "") {
      validationErrors.currentPassword = "Current password is required.";
    } else {
      validationErrors.currentPassword = "";
    }

    // Validate the new password
    if (name === "newPassword") {
      if (!passwordRegex.test(value)) {
        validationErrors.newPassword =
          "Password must be at least 8 characters long, contain at least one uppercase letter, one number, and one special character.";
      } else {
        validationErrors.newPassword = ""; // Clear error if valid
      }

      // Ensure confirmPassword validation is updated as newPassword changes
      if (formData.confirmPassword && formData.confirmPassword !== value) {
        validationErrors.confirmPassword =
          "New password and Confirm password must match.";
      } else {
        validationErrors.confirmPassword = "";
      }
    }

    // Validate the confirm password
    if (name === "confirmPassword") {
      if (value !== formData.newPassword) {
        validationErrors.confirmPassword =
          "New password and Confirm password must match.";
      } else {
        validationErrors.confirmPassword = "";
      }
    }

    setErrors(validationErrors);
  };

  const handleLogout = () => {
    // Clear user session data
    localStorage.clear();
    sessionStorage.clear();

    // Replace the history state to prevent back navigation
    window.history.replaceState(null, null, "/signin");

    // Forcefully reload the page to clear all states
    setTimeout(() => {
      window.location.href = "/signin";
    }, 100); // Slight delay to ensure changes take effect
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/auth/change-password`,
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      handleLogout();
    } catch (error) {
      // console.error("Error changing password:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClick = () => {
    setFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    // Reset label positions and styles
    const fields = document.querySelectorAll(".relative");
    fields.forEach((field) => {
      const label = field.querySelector("label");
      const input = field.querySelector("input");
      const pLabel = field.querySelector("p");

      // Reset label position
      if (label) {
        label.classList.remove("float");
      }

      // Reset input border styles
      if (input) {
        input.classList.remove("focus-border", "error-border");
      }

      // Reset error message styles
      if (pLabel) {
        pLabel.classList.remove("text-red-500");
        pLabel.classList.add("text-gray-500");
      }
    });
  };

  return (
    <>
      <div className="flex flex-col space-y-6 w-[72%] text-left">
        {isSubmitting && <Loader />}
        <div className="px-10 py-6 bg-white rounded shadow-lg">
          {/* <!-- Change password --> */}
          <h2 className="text-gray-800 text-lg font-semibold mb-4">
            Change Password{" "}
            <span className="text-gray-500 text-base font-thin pl-2">
              change or reset your account password
            </span>
          </h2>
          <hr className="border-t border-gray-300 mb-4" />
          <div className="flex items-start bg-blue-100 border border-blue-300 text-blue-700 px-4 py-3 rounded mt-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 flex-shrink-0 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 20h.01M12 20h-2a2 2 0 01-2-2V6a2 2 0 012-2h4a2 2 0 012 2v12a2 2 0 01-2 2h-2zm0-18a2 2 0 00-2-2v.01a2 2 0 002 2zm0 4a2 2 0 00-2-2v.01a2 2 0 002 2z"
              />
            </svg>
            <div>
              <span className="text-sm">
                Once your password is updated, you will be logged out of Portal!
              </span>
              <span className="text-sm">
                {" "}
                You will need to Sign In back with the updated password.
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 mt-6">
            {/* <!-- currentPassword --> */}
            <div className="relative">
              <label
                htmlFor="currentPassword"
                className="absolute text-sm text-gray-500 top-2 left-3 bg-white px-1"
              >
                Current Password
              </label>
              <PasswordInput
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
                value={formData.currentPassword}
                onChange={handleChange}
                inputClassName="p-2 border rounded focus:outline-none"
              />
              <p
                className={`text-xs mt-1 ${
                  errors.currentPassword ? "text-red-500" : "text-gray-500"
                }`}
              >
                {"Please enter the current password" +
                  " " +
                  (errors.currentPassword ? `: ${errors.currentPassword}` : "")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 mt-6">
            {/* <!-- newPassword --> */}
            <div className="relative">
              <label
                htmlFor="newPassword"
                className="absolute text-sm text-gray-500 top-2 left-3 bg-white px-1"
              >
                New Password
              </label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                value={formData.newPassword}
                onChange={handleChange}
                inputClassName="p-2 border rounded focus:outline-none"
              />
              <p
                className={`text-xs mt-1 ${
                  errors.newPassword ? "text-red-500" : "text-gray-500"
                }`}
              >
                {errors.newPassword ||
                  "Password must be at least 8 characters long, contain at least one uppercase letter, one number, and one special character."}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 mt-6">
            {/* <!-- confirmPassword --> */}
            <div className="relative">
              <label
                htmlFor="confirmPassword"
                className="absolute text-sm text-gray-500 top-2 left-3 bg-white px-1"
              >
                Confirm Password
              </label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                inputClassName="p-2 border rounded focus:outline-none"
              />
              <p
                className={`text-xs mt-1 ${
                  errors.confirmPassword ? "text-red-500" : "text-gray-500"
                }`}
              >
                {"Please enter password for confirmation" +
                  " " +
                  (errors.currentPassword ? `: ${errors.currentPassword}` : "")}
              </p>
            </div>

            <div className="flex justify-start items-start space-x-4 mt-14 mr-5">
              {/* <!-- Save & Close and Close buttons --> */}
              <button
                onClick={handleSubmit}
                disabled={
                  !formData.currentPassword ||
                  !formData.newPassword ||
                  !formData.confirmPassword ||
                  Object.values(errors).some((error) => error)
                }
                className={`px-4 py-2 rounded border 
                ${
                  formData.currentPassword &&
                  formData.newPassword &&
                  formData.confirmPassword
                    ? "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                    : "bg-white text-blue-200 border-blue-200 cursor-not-allowed"
                }`}
              >
                Change Password
              </button>
              <button
                onClick={handleCancelClick}
                className="bg-white text-gray-500 border border-gray-500 px-4 py-2 rounded hover:bg-gray-500 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ChangePassword;
